import { useCallback } from 'react';
import { ga4Tracker, extractVehicleData } from '../utils/ga4Tracking';
import type { ContactData } from './useContactExtraction';

export const useAnalyticsTracking = () => {
  const trackFormCompletion = useCallback((
    formType: string, 
    contactData: ContactData, 
    interestData?: any
  ) => {
    const vehicleData = interestData?.vehicle ? extractVehicleData(interestData.vehicle) : undefined;
    
    // Track form completion
    ga4Tracker.trackFormComplete(formType, vehicleData);
    
    // Track specific automotive events based on form type
    if (Object.keys(contactData).length > 0 && (contactData.Name || contactData.Phone || contactData.Email)) {
      const leadData = {
        lead_type: formType,
        contact_method: 'chatbot_form',
        customer_name: contactData.Name,
        customer_phone: contactData.Phone,
        customer_email: contactData.Email,
        interest_vehicle: vehicleData
      };
      
      ga4Tracker.trackLeadGeneration(leadData);
      
      // Track specific form type events
      switch (formType) {
        case 'service':
          ga4Tracker.trackServiceSchedule({
            service_type: 'general_service',
            vehicle: vehicleData
          });
          break;
        case 'appointment':
          ga4Tracker.trackAppointmentSchedule({
            appointment_type: 'general_appointment',
            vehicle: vehicleData
          });
          break;
        case 'finance':
        case 'financev1':
          ga4Tracker.trackFinancingRequest({
            application_type: 'credit_application',
            vehicle: vehicleData
          });
          break;
        case 'trade':
          ga4Tracker.trackTradeEstimate({
            interest_vehicle: vehicleData
          });
          break;
        case 'carfax':
          if (vehicleData) {
            ga4Tracker.trackCarfaxRequest(vehicleData);
          }
          break;
        case 'contact':
        case 'offer':
        default:
          ga4Tracker.trackContactDealer({
            contact_type: formType,
            vehicle: vehicleData
          });
          break;
      }
    }
  }, []);

  const trackChatInteraction = useCallback((type: 'user_message' | 'button_click', messageType: 'text_input' | 'quick_reply') => {
    ga4Tracker.trackChatInteraction({
      interaction_type: type,
      message_type: messageType
    });
  }, []);

  // D2C Chatbot specific tracking methods
  const trackChatbotImpression = useCallback(() => {
    ga4Tracker.trackChatbotImpression();
  }, []);

  const trackChatbotClick = useCallback(() => {
    ga4Tracker.trackChatbotClick();
  }, []);

  const trackChatStarted = useCallback(() => {
    ga4Tracker.trackChatStarted();
  }, []);

  const trackUserMessageSent = useCallback((messageLength?: number) => {
    ga4Tracker.trackUserMessageSent(messageLength);
  }, []);

  const trackAgentMessageSent = useCallback((messageLength?: number) => {
    ga4Tracker.trackAgentMessageSent(messageLength);
  }, []);

  const trackLeadSent = useCallback((leadType: string, leadData?: any) => {
    ga4Tracker.trackLeadSent(leadType, leadData);
  }, []);

  return {
    trackFormCompletion,
    trackChatInteraction,
    trackChatbotImpression,
    trackChatbotClick,
    trackChatStarted,
    trackUserMessageSent,
    trackAgentMessageSent,
    trackLeadSent
  };
};